import { DateFilter, datePeriodEnumToJSON, DateFilterType, monthEnumToJSON, quarterEnumToJSON, GrowthBenchmarkType } from "build-ts/relative_date";
import { epochConverterSecond } from "./dateUtils";
import { AggregationType, aggregationTypeToJSON } from "build-ts/enums";
import { FilterProto, FilterProto_CalculatedDateRange } from "build-ts/metadata";
import { getOperatorSymbol } from "./analysisUtils";
import { StatusInfo } from "modules/analyst/containers/AnalysisRunCard";
import { JobRunStatus } from "build-ts/schedule";

export const generateStepName = (elements: string[]): string => {

  // Handle cases where elements is undefined, not an array, or empty
  if (!elements || !Array.isArray(elements) || elements.length === 0) {
    return "";
  }

  // 1. Safely take the metric from the first element, if it exists
  const metric = elements[0] || "";

  // 2. Safely process the second element, handle cases where it's undefined or empty
  if (!elements[1]) {
    return metric;
  }

  const items = elements[1].split(",").map(item => item.trim()).filter(item => item !== "");
  const processedItems = items.map(item => {
    const itemParts = item.split(" ");

    // If it's a single word, take up to the first three characters
    if (itemParts.length === 1) {
      return itemParts[0].slice(0, 3);
    }
    // If it's a two-word item, safely take the first two letters of the first word and the first letter of the second word
    else if (itemParts.length >= 2) {
      const firstWord = itemParts[0];
      const secondWord = itemParts[1];
      const firstPart = firstWord.slice(0, 2) || "";  // Ensure first two letters of first word
      const secondPart = secondWord.charAt(0) || "";  // Ensure first letter of second word
      return `${firstPart}${secondPart}`;
    }
    // Handle cases with more than two words (if needed)
    return itemParts.map(part => part.charAt(0)).join("");
  });

  // Combine all parts
  const result = `${metric} | ${processedItems.join("/")}`;
  return result;
};

export const getDateFilterName = (dateFilter: DateFilter): string => {
  if (!dateFilter) {
    return "";
  }

  if (dateFilter.type === undefined || dateFilter.type === DateFilterType.UNRECOGNIZED) {
    return "Invalid";
  }

  // Helper function to get "as of" date string using epochConverterSecond or current date
  const getAsOfDateString = (): string => {
    if (dateFilter.reference_date_epoc_sec) {
      return epochConverterSecond(dateFilter.reference_date_epoc_sec);
    }
    return "";
  };

  switch (dateFilter.type) {
    case DateFilterType.LAST_N_PERIOD: {
      const asOfDateStr = getAsOfDateString();
      const period = dateFilter.date_period !== undefined
        ? datePeriodEnumToJSON(dateFilter.date_period).charAt(0)
        : "";
      const number = dateFilter.number ?? "";
      return asOfDateStr ? `Last ${number}${period} (as of ${asOfDateStr})` : `Last ${number}${period}`;
    }
    case DateFilterType.NEXT_N_PERIOD: {
      const asOfDateStr = getAsOfDateString();
      const period = dateFilter.date_period !== undefined
        ? datePeriodEnumToJSON(dateFilter.date_period).charAt(0)
        : "";
      const number = dateFilter.number ?? "";
      return asOfDateStr ? `Next ${number}${period} (as of ${asOfDateStr})` : `Next ${number}${period}`;
    }
    case DateFilterType.THIS_PERIOD: {
      const asOfDateStr = getAsOfDateString();
      const period = dateFilter.date_period !== undefined
        ? datePeriodEnumToJSON(dateFilter.date_period).toLowerCase()
        : "";
      return asOfDateStr ? `This ${period} (as of ${asOfDateStr})` : `This ${period}`;
    }
    case DateFilterType.EXACT_DATE_RANGE: {
      if (dateFilter.date_range?.low_epoch_sec === undefined ||
        dateFilter.date_range?.high_epoch_sec === undefined) {
        return "Invalid Date Range";
      }
      const lowDate = epochConverterSecond(dateFilter.date_range.low_epoch_sec);
      const highDate = epochConverterSecond(dateFilter.date_range.high_epoch_sec);
      if (lowDate === highDate) {
        return `${lowDate}`;
      }
      return `Between ${lowDate} to ${highDate}`;
    }
    case DateFilterType.EXACT_DATE: {
      if (dateFilter.reference_date_epoc_sec === undefined) {
        return "Invalid Date";
      }
      const exactDate = epochConverterSecond(dateFilter.reference_date_epoc_sec);
      return `${exactDate}`;
    }
    case DateFilterType.PERIOD_TO_DATE: {
      const asOfDateStr = getAsOfDateString();
      const period = dateFilter.date_period !== undefined
        ? datePeriodEnumToJSON(dateFilter.date_period).charAt(0)
        : "";
      return asOfDateStr ? `${period}TD (as of ${asOfDateStr})` : `${period}TD`;
    }
    case DateFilterType.MONTH_YEAR: {
      const monthStr = monthEnumToJSON(dateFilter.month!);
      const monthFormatted =
        monthStr.charAt(0) + monthStr.slice(1, 3).toLowerCase();
      return `${monthFormatted} ${dateFilter.year}`;
    }
    case DateFilterType.QUARTER_YEAR: {
      if (dateFilter.quarter === undefined || dateFilter.year === undefined) {
        return "Invalid Quarter/Year";
      }
      const quarterStr = quarterEnumToJSON(dateFilter.quarter);
      return `${quarterStr} ${dateFilter.year}`;
    }
    case DateFilterType.YEAR_ONLY: {
      if (dateFilter.year === undefined) {
        return "Invalid Year";
      }
      return `${dateFilter.year}`;
    }
    default:
      return "Unknown Date Range type";
  }
};

/**
 * Gets the string names of an enum, excluding specific keys.
 * 
 * @param enumObj - The enum object to process.
 * @returns A list of enum string names excluding specified keys.
 */
export const getEnumNames = <T extends { [key: string]: number | string }>(enumObj: T): string[] => {
  const excludedKeys = ["UNRECOGNIZED"];
  return Object.keys(enumObj)
    .filter((key) => isNaN(Number(key)))
    .filter((key) => !excludedKeys.includes(key));
};

/**
 * Returns a label for a given growth benchmark type.
 * 
 * @param growthBenchmarkType - The type of growth benchmark. Determines the format of the label.
 * @param number - (Optional) A numeric value representing the number of periods for aggregation.
 * @param aggregationType - (Optional) The type of aggregation to be used. 
 * @returns A string label for the growth benchmark:
 *          - "Prior Period" for .GrowthBenchmarkType.PRIOR_PERIOD.
 *          - "Last N Periods AggregationType" for GrowthBenchmarkType.LAST_N_PERIOD_AGGREGATION.
 */
export const getBenchmarkLabel = (
  growthBenchmarkType: GrowthBenchmarkType,
  number?: number,
  aggregationType?: AggregationType
): string => {
  switch (growthBenchmarkType) {
    case GrowthBenchmarkType.PRIOR_PERIOD:
      return "Prior Period";
    case GrowthBenchmarkType.PRIOR_YEAR:
      return "Prior Year";
    case GrowthBenchmarkType.LAST_N_PERIOD_AGGREGATION:
      const aggregation = aggregationTypeToJSON(aggregationType!);
      return `Last ${number ?? "N"} Periods ${aggregation.charAt(0) + aggregation.slice(1).toLowerCase()}`;
    default:
      return "Unknown Benchmark";
  }
};

/**
 * Creates UI specific filter values string to display.
 *
 * @param filter - FilterProto.
 * @returns A string representation of the filter.
 */
export const getFilterDisplayString = (filter: FilterProto): string => {
  if (filter?.date_filter) {
    return getDateFilterName(filter?.date_filter!);
  } else if (filter?.values?.length > 0) {
    return filter.values.join(", ");
  } else if (filter?.calculated_date_ranges?.length > 0) {
    return filter.calculated_date_ranges
      .map((date: FilterProto_CalculatedDateRange) => {
        const operatorSymbol = getOperatorSymbol(date?.operator!);
        const formattedDate = epochConverterSecond(date?.date_epoch_sec!);
        return `${operatorSymbol} ${formattedDate}`;
      })
      .join(", ");
  } else {
    return "Selected Values";
  }
};

/**
 * Converts an input string from uppercase with underscores (e.g., "LAST_WEEK")
 * to sentence case (e.g., "Last week").
 *
 * - Replaces underscores with spaces
 * - Converts the entire string to lowercase
 * - Capitalizes only the first letter
 *
 * @param input - The input string to format.
 * @returns A formatted string in sentence case.
 */
export const formatToSentenceCase = (input: string | null | undefined): string => {
  if (typeof input !== "string") return "";

  // Replace underscores with spaces and convert to lowercase
  const sentence = input.replace(/_/g, " ").toLowerCase();

  // Capitalize only the first letter
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
};

/**
 * Generates a random suffix of a given length.
 *
 * @param length - The length of the suffix.
 * @returns A random suffix.
 */
export const generateRandomSuffix = (length: number): string => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

// Helper function to convert a string to a seed
// It is an DJB2 hash function
const stringToSeed = (str: string): number => {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return Math.abs(hash);
};

/**
 * Returns a action verb for the given action type from the ACTION_VERBS object.
 * 
 * @param actionType - The type of action to get a verb for.
 * @param name - The name to use for the random number generator.
 * @param index - The index to use for the random number generator.
 * @returns A action verb for the given action type.
 */
export const getRandomActionVerb = (verbs: string[], name: string, index: number): string => {
  const nameSeed = stringToSeed(name);
  const combinedSeed = nameSeed + index; // Combine name and index
  return verbs[combinedSeed % verbs.length];
};

/**
 * Returns a random welcome message. When a first name is provided, some
 * messages will include it naturally (mirroring how Claude / GPT greet users);
 * others stay generic so the name never feels forced.
 *
 * @param firstName - Optional first name of the current user.
 * @returns A randomly selected welcome message string.
 */
export const getRandomWelcomeMessage = (name?: string): string => {
  const generic = [
    "What are you working on?",
    "Where should we begin?",
    "What can I help with?",
    "Ready when you are.",
  ];

  const personalized = name?.trim()
    ? [
      `Good to see you, ${name}.`,
      `Hey, ${name}. Ready to dive in?`,
      `What's on your mind, ${name}?`,
      `What can I help you with today, ${name}?`,
    ]
    : [];

  const all = [...generic, ...personalized];
  return all[Math.floor(Math.random() * all.length)];
};

/**
 * Returns a status info object with the color and label for the given status.
 * 
 * @param status - The status to get the info for.
 * @returns A status info object with the color and label.
 */
export const getStatusInfo = (status: JobRunStatus): StatusInfo => {
  switch (status) {
    case JobRunStatus.PENDING:
      return { color: "gray", label: "Pending" };
    case JobRunStatus.IN_PROGRESS:
      return { color: "blue", label: "In Progress" };
    case JobRunStatus.SUCCESS:
      return { color: "green", label: "Success" };
    case JobRunStatus.FAILURE:
      return { color: "red", label: "Failed" };
    case JobRunStatus.PARTIAL_SUCCESS:
      return { color: "orange", label: "Partial Success" };
    default:
      return { color: "gray", label: "Unknown" };
  }
};
